import { toast } from "sonner";

// Define the type for case study data
export interface CaseStudy {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  results: {
    label: string;
    value: string;
    change?: string;
    positive?: boolean;
  }[];
  link: string;
  featured?: boolean;
}

// Function to fetch case studies from losangeleseoco.com
export const fetchCaseStudies = async (): Promise<CaseStudy[]> => {
  try {
    // Make a request to the website
    const response = await fetch("https://losangeleseoco.com/api/case-studies");
    
    // Check if the request was successful
    if (!response.ok) {
      throw new Error(`Failed to fetch case studies: ${response.status}`);
    }
    
    // Parse the JSON response
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching case studies:", error);
    // Return fallback data if the fetch fails, without notifying the user
    return getFallbackCaseStudies();
  }
};

// Fallback case studies data in case the API request fails
const getFallbackCaseStudies = (): CaseStudy[] => {
  return [
    {
      id: "cs-1",
      title: "Sunset Boulevard Retail Chain",
      description: "A multi-location retail business struggling with local visibility saw a 215% increase in store visits after our local SEO campaign, outranking major competitors for valuable local keywords.",
      image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Local SEO",
      results: [
        { label: "Increase in Store Visits", value: "215%", change: "215%", positive: true },
        { label: "Google Maps Views", value: "11,600", change: "340%", positive: true },
        { label: "Local Keyword Rankings", value: "#1-3", change: "21 Keywords", positive: true },
        { label: "Return on Investment", value: "410%", change: "410%", positive: true }
      ],
      link: "/case-studies/sunset-boulevard-retail",
      featured: true
    },
    {
      id: "cs-2",
      title: "Canyon Healthcare Provider",
      description: "A healthcare provider with multiple locations across LA County needed to improve their online presence and patient acquisition. Our comprehensive SEO strategy delivered exceptional results.",
      image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Healthcare SEO",
      results: [
        { label: "Organic Traffic", value: "189%", change: "189%", positive: true },
        { label: "New Patient Inquiries", value: "127%", change: "127%", positive: true },
        { label: "First Page Keywords", value: "76", change: "47 Keywords", positive: true },
        { label: "Conversion Rate", value: "13.4%", change: "6.2%", positive: true }
      ],
      link: "/case-studies/canyon-healthcare",
      featured: false
    },
    {
      id: "cs-3",
      title: "Desert Bloom E-commerce",
      description: "An LA-based e-commerce store selling handcrafted home decor items was struggling with low traffic and poor conversion rates. Our customized SEO approach transformed their online business.",
      image: "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "E-commerce SEO",
      results: [
        { label: "Organic Revenue", value: "314%", change: "314%", positive: true },
        { label: "Conversion Rate", value: "8.7%", change: "4.3%", positive: true },
        { label: "Product Page Traffic", value: "278%", change: "278%", positive: true },
        { label: "Average Order Value", value: "$142", change: "37%", positive: true }
      ],
      link: "/case-studies/desert-bloom-ecommerce",
      featured: false
    },
    {
      id: "cs-4",
      title: "Beverly Hills Law Firm",
      description: "A prestigious law firm specializing in entertainment law was losing potential clients to competitors online. Our legal SEO strategy helped them dominate search results for high-value practice areas.",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Legal SEO",
      results: [
        { label: "Case Inquiries", value: "243%", change: "243%", positive: true },
        { label: "Traffic from Legal Searches", value: "186%", change: "186%", positive: true },
        { label: "Keyword Rankings", value: "52", change: "34 Keywords", positive: true },
        { label: "Conversion Rate", value: "12.2%", change: "7.8%", positive: true }
      ],
      link: "/case-studies/beverly-hills-law",
      featured: true
    },
    {
      id: "cs-5",
      title: "Santa Monica Restaurant Group",
      description: "A restaurant group with 5 locations in the Santa Monica area was struggling to attract diners through organic search. Our local restaurant SEO strategy increased bookings and foot traffic significantly.",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Restaurant SEO",
      results: [
        { label: "Online Reservations", value: "164%", change: "164%", positive: true },
        { label: "Local Search Visibility", value: "258%", change: "258%", positive: true },
        { label: "Google Reviews", value: "127", change: "73 New", positive: true },
        { label: "Weekend Revenue", value: "32%", change: "32%", positive: true }
      ],
      link: "/case-studies/santa-monica-restaurants",
      featured: false
    },
    {
      id: "cs-6",
      title: "Silicon Beach E-commerce Platform",
      description: "An e-commerce startup based in the Silicon Beach area was struggling to compete with established brands. Our technical SEO and content strategy helped them capture valuable market share.",
      image: "https://images.unsplash.com/photo-1526570207772-784d36084510?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "E-commerce SEO",
      results: [
        { label: "Organic Revenue", value: "326%", change: "326%", positive: true },
        { label: "Shopping Traffic", value: "215%", change: "215%", positive: true },
        { label: "Product Keywords", value: "94", change: "Top 10 Results", positive: true },
        { label: "Cart Abandonment", value: "-42%", change: "-42%", positive: true }
      ],
      link: "/case-studies/silicon-beach-ecommerce",
      featured: false
    },
    {
      id: "cs-7",
      title: "Pasadena Tech Startup",
      description: "A B2B SaaS startup based in Pasadena needed to increase their visibility to enterprise clients. Our tech SEO strategy helped them rank for high-value, high-intent keywords in their niche.",
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Technology SEO",
      results: [
        { label: "Enterprise Leads", value: "218%", change: "218%", positive: true },
        { label: "Demo Requests", value: "176%", change: "176%", positive: true },
        { label: "Technical Keywords", value: "83", change: "47 Keywords", positive: true },
        { label: "Organic Traffic Value", value: "$87K", change: "312%", positive: true }
      ],
      link: "/case-studies/pasadena-tech-startup",
      featured: false
    },
    {
      id: "cs-8",
      title: "Malibu Fitness Center",
      description: "A premium fitness center in Malibu wanted to increase memberships and class bookings. Our fitness industry SEO strategy helped them become the dominant fitness brand in their area.",
      image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Fitness SEO",
      results: [
        { label: "Membership Inquiries", value: "284%", change: "284%", positive: true },
        { label: "Class Bookings", value: "193%", change: "193%", positive: true },
        { label: "Local Fitness Keywords", value: "94%", change: "Top 3 Results", positive: true },
        { label: "Organic Lead Cost", value: "-68%", change: "-68%", positive: true }
      ],
      link: "/case-studies/malibu-fitness-center",
      featured: false
    },
    {
      id: "cs-9",
      title: "Downtown LA Financial Advisor",
      description: "A financial advisory firm in Downtown LA needed to attract high-net-worth clients in a highly regulated industry. Our finance SEO strategy helped them build trust and visibility with their target audience.",
      image: "https://images.unsplash.com/photo-1444653614773-995cb1ef9efa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Finance SEO",
      results: [
        { label: "Qualified Consultations", value: "156%", change: "156%", positive: true },
        { label: "Financial Planning Traffic", value: "212%", change: "212%", positive: true },
        { label: "Trust Keywords", value: "37", change: "Top 10 Results", positive: true },
        { label: "Client Acquisition Cost", value: "-42%", change: "-42%", positive: true }
      ],
      link: "/case-studies/downtown-la-financial",
      featured: false
    },
    {
      id: "cs-10",
      title: "Long Beach Manufacturing Company",
      description: "A manufacturing company in Long Beach needed to generate B2B leads and showcase their capabilities to potential clients. Our industrial SEO strategy helped them connect with decision-makers in their target industries.",
      image: "https://images.unsplash.com/photo-1586528116493-7f9f30c85083?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Manufacturing SEO",
      results: [
        { label: "B2B Lead Generation", value: "178%", change: "178%", positive: true },
        { label: "Industry Traffic", value: "243%", change: "243%", positive: true },
        { label: "Technical Keywords", value: "64", change: "First Page", positive: true },
        { label: "RFQ Submissions", value: "189%", change: "189%", positive: true }
      ],
      link: "/case-studies/long-beach-manufacturing",
      featured: false
    },
    {
      id: "cs-11",
      title: "Venice Beach Arts Foundation",
      description: "A non-profit arts foundation in Venice Beach needed to increase awareness, donations, and event attendance. Our non-profit SEO strategy helped them connect with art enthusiasts and potential donors.",
      image: "https://images.unsplash.com/photo-1459908676235-d5f02a50184b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Non-Profit SEO",
      results: [
        { label: "Online Donations", value: "167%", change: "167%", positive: true },
        { label: "Event Registrations", value: "214%", change: "214%", positive: true },
        { label: "Community Keywords", value: "42", change: "Top Results", positive: true },
        { label: "Volunteer Sign-ups", value: "193%", change: "193%", positive: true }
      ],
      link: "/case-studies/venice-beach-arts",
      featured: false
    },
    {
      id: "cs-12",
      title: "Culver City Education Institute",
      description: "An educational institute in Culver City offering professional certification programs needed to attract more students. Our education SEO strategy helped them increase enrollments across multiple programs.",
      image: "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Education SEO",
      results: [
        { label: "Program Enrollments", value: "231%", change: "231%", positive: true },
        { label: "Course Information Traffic", value: "276%", change: "276%", positive: true },
        { label: "Education Keywords", value: "67", change: "First Page", positive: true },
        { label: "Cost Per Enrollment", value: "-53%", change: "-53%", positive: true }
      ],
      link: "/case-studies/culver-city-education",
      featured: false
    },
    {
      id: "cs-13",
      title: "Hollywood Entertainment Agency",
      description: "A talent and management agency in Hollywood needed to increase their client roster and industry presence. Our entertainment industry SEO strategy helped them stand out in a crowded market.",
      image: "https://images.unsplash.com/photo-1586899028174-e7098604235b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
      category: "Entertainment SEO",
      results: [
        { label: "Talent Submissions", value: "342%", change: "342%", positive: true },
        { label: "Industry Traffic", value: "203%", change: "203%", positive: true },
        { label: "Entertainment Keywords", value: "49", change: "Top 10", positive: true },
        { label: "Brand Mentions", value: "187%", change: "187%", positive: true }
      ],
      link: "/case-studies/hollywood-entertainment",
      featured: false
    }
  ];
};
